import type { KeyEventArgs } from "../core/events.js";
import { clamp, insets, rectDeflate, type Rect } from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import { paintGlyph } from "../themes/glyphRegistry.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import { Button, type ButtonOptions } from "./Button.js";
import { drawAlignedText } from "./Label.js";
import type { MenuItemSpec } from "./MenuItem.js";
import { fitInWorkArea, MenuPopup } from "./MenuPopup.js";

/**
 * What the button's face shows: any name from the glyph registry, or "none"
 * for a caption on its own.
 */
export type MenuButtonGlyph = string;

/** Which corner of the button the menu is anchored to. */
export type MenuAlign = "left" | "right";

export interface MenuButtonOptions extends ButtonOptions {
  /**
   * The rows to show. A function is called each time the menu opens, which is
   * what you want whenever the rows carry current state — a tick beside the
   * active item, a list that has changed since last time.
   */
  menu?: MenuItemSpec[] | (() => MenuItemSpec[]);
  glyph?: MenuButtonGlyph;
  /** Edge of the button the menu lines up with. */
  menuAlign?: MenuAlign;
  /** Open above the button instead of below. */
  dropUp?: boolean;
}

const GLYPH_GAP = 6;
const GLYPH_BOX = 18;
/** Inset of a caption-less glyph from the button's edge. */
const GLYPH_INSET = 6;

/** Classic nudges a button's content down-right while it is held. */
function shift(r: Rect, by: number): Rect {
  return by === 0 ? r : { ...r, x: r.x + by, y: r.y + by };
}

/**
 * A button that opens a menu instead of raising a bare click.
 *
 * With `glyph: "hamburger"` and no text it is the usual hamburger: a menu
 * hanging off a button, which is all a hamburger has ever been. With a
 * caption and `glyph: "arrow"` it is a drop-down button. `Toolbar` has its own
 * drop-down items; this is the standalone control for everywhere else.
 *
 * The face is drawn over `theme.drawButton`, so it comes out a Win95 bevel
 * under Classic and a rounded plate under Fluent without knowing about
 * either.
 */
export class MenuButton extends Button {
  #menu: MenuItemSpec[] | (() => MenuItemSpec[]);
  #open: MenuPopup | null = null;

  glyph: MenuButtonGlyph;
  menuAlign: MenuAlign;
  dropUp: boolean;

  constructor(options: MenuButtonOptions = {}) {
    super({ width: 36, height: 32, textAlign: "left", ...options });
    this.#menu = options.menu ?? [];
    // A captioned menu button is a drop-down and wears a chevron; a bare one
    // is a hamburger.
    this.glyph = options.glyph ?? (options.text ? "chevron-down" : "hamburger");
    this.menuAlign = options.menuAlign ?? "left";
    this.dropUp = options.dropUp ?? false;

    this.on("click", () => this.openMenu());
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  /** The rows, or the function that produces them. */
  get menu(): MenuItemSpec[] | (() => MenuItemSpec[]) {
    return this.#menu;
  }
  set menu(v: MenuItemSpec[] | (() => MenuItemSpec[])) {
    this.#menu = v;
    this.invalidate();
  }

  /** The rows as they stand right now. */
  menuItems(): MenuItemSpec[] {
    return typeof this.#menu === "function" ? this.#menu() : this.#menu;
  }

  /** True while its menu is showing. */
  get droppedDown(): boolean {
    return this.#open !== null;
  }

  /** Where the menu's top-left corner goes, in screen coordinates. */
  protected menuOrigin(size: { width: number; height: number }): { x: number; y: number } {
    const b = this.screenBounds;
    return {
      x: this.menuAlign === "right" ? b.x + b.width - size.width : b.x,
      y: this.dropUp ? b.y - size.height : b.y + b.height,
    };
  }

  openMenu(): MenuPopup | null {
    const desktop = this.desktop;
    if (!desktop || this.#open || !this.effectivelyEnabled) return null;
    const items = this.menuItems();
    if (items.length === 0) return null;

    // Built rather than handed to `showMenu`, because a right-aligned or
    // upward menu has to be measured before its corner can be worked out, and
    // `showMenu` only ever hangs one down-and-right of a point.
    const menu = new MenuPopup({ items });
    // Capped and clamped to the work area: a hamburger usually sits in a
    // reserved strip, and a menu that hung back over that strip would cover
    // the very button that opened it.
    const size = menu.measureIn(desktop);
    const at = fitInWorkArea(desktop, this.menuOrigin(size), size);
    menu.setBounds(at.x, at.y, size.width, size.height);
    desktop.showPopup(menu);
    menu.focus();

    menu.on("closed", () => {
      if (this.#open === menu) {
        this.#open = null;
        this.invalidate();
      }
    });
    this.#open = menu;
    this.invalidate();
    return menu;
  }

  closeMenu(): void {
    const menu = this.#open;
    this.#open = null;
    if (menu) this.desktop?.closePopup(menu);
  }

  /**
   * Held-in while the menu is down.
   *
   * A press that lands outside an open popup is swallowed by the desktop, so
   * tapping the button again closes the menu and never reaches the click that
   * would reopen it. That is the behavior wanted, and it comes for free.
   */
  override visualState(): VisualState {
    const state = super.visualState();
    return this.#open ? { ...state, pressed: true } : state;
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (e.handled || !this.focused) return;
    // Alt+Down is the Windows gesture for "open this thing's list".
    if ((e.key === "ArrowDown" && e.altKey) || (e.key === "F4" && !e.altKey)) {
      this.openMenu();
      e.handled = true;
    }
  }

  /**
   * Box the glyph is drawn in.
   *
   * Beside a caption it is a fixed square at the right of the text area. On
   * its own it gets the whole face, inset only enough to clear the border —
   * `buttonPadding` is sized for text, and a hamburger squeezed into it comes
   * out as three specks.
   */
  protected glyphRect(content: Rect, full: Rect): Rect {
    if (this.text) {
      return {
        x: content.x + content.width - GLYPH_BOX,
        y: content.y,
        width: GLYPH_BOX,
        height: content.height,
      };
    }
    return rectDeflate(full, insets(GLYPH_INSET));
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const state = this.visualState();
    const r = this.localRect;
    theme.drawButton(rc, r, state);

    const nudge = state.pressed ? 1 : 0;
    const content = shift(rectDeflate(r, theme.metrics.buttonPadding), nudge);
    const ink = theme.buttonTextColor(state);

    const box = this.glyphRect(content, shift(r, nudge));
    // A drop-up points up: swap the default chevron rather than making every
    // caller remember to.
    const glyph =
      this.dropUp && this.glyph === "chevron-down" ? "chevron-up" : this.glyph;
    if (glyph && glyph !== "none") {
      paintGlyph(glyph, {
        rc,
        box,
        ink,
        weight: Math.max(1, Math.round(Math.min(box.width, box.height) / 10)),
      });
    }

    if (!this.text) return;
    const width = this.glyph === "none" ? content.width : content.width - GLYPH_BOX - GLYPH_GAP;
    drawAlignedText(
      rc,
      this.text,
      { ...content, width: Math.max(0, width) },
      theme.metrics.font,
      ink,
      this.textAlign,
      "middle",
      true,
    );
  }
}
